/**
 * tafPlainLanguage.ts
 * Builds a plain-language French TAF timeline for leisure pilots.
 * Merges TEMPO/PROB into their parent period, translates all codes,
 * produces human-readable local-time titles.
 */

export type RiskLevel = 'Favorable' | 'À surveiller' | 'Prudence' | 'Risque accru';
export type IconName = 'thunderstorm' | 'rain' | 'showers' | 'mist' | 'fog' | 'snow' | 'cloud' | 'partlyCloudy' | 'clear' | 'alert' | 'freezingRain';

export interface PlainTafPeriod {
  id: string;
  startZulu: Date | null;
  endZulu: Date | null;
  startLocal: string;   // e.g. "20 juin 11:00"
  endLocal: string;     // e.g. "20 juin 16:00" or "" if open-ended
  title: string;        // e.g. "Du 20 juin 11:00 à 12:00 locale"
  zuluLabel: string;    // e.g. "Zoulou : 20 juin 15:00Z → 20 juin 16:00Z"
  summary: string;      // plain-French summary sentence
  windText: string;
  visibilityText: string;
  cloudsText: string;
  weatherText: string;
  temporaryText: string;   // TEMPO description in plain French
  probabilityText: string; // PROB description in plain French
  watchItems: string[];    // items to watch
  riskLevel: RiskLevel;
  iconName: IconName;
  badges: string[];        // human-readable badges
}

// ─── Month names ──────────────────────────────────────────────────────────────

const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MONTHS_FR_SHORT = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin',
  'juil', 'août', 'sep', 'oct', 'nov', 'déc'];

// ─── Date helpers ─────────────────────────────────────────────────────────────

function buildUTCDate(dd: number, hh: number, mm: number, refDate: Date): Date {
  const year = refDate.getUTCFullYear();
  const month = refDate.getUTCMonth();
  const currentDay = refDate.getUTCDate();
  let m = month;
  let y = year;
  if (dd > 20 && currentDay < 10) {
    m = month - 1;
    if (m < 0) { m = 11; y--; }
  } else if (dd < 10 && currentDay > 20) {
    m = month + 1;
    if (m > 11) { m = 0; y++; }
  }
  return new Date(Date.UTC(y, m, dd, hh, mm, 0));
}

function fmtZulu(d: Date): string {
  const day = d.getUTCDate();
  const mon = MONTHS_FR_SHORT[d.getUTCMonth()];
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day} ${mon} ${hh}:${mm}Z`;
}

function fmtLocal(d: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('fr-CA', {
      timeZone: 'America/Toronto',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const day = parts.find(p => p.type === 'day')?.value ?? '';
    const mon = parts.find(p => p.type === 'month')?.value ?? '';
    const hour = parts.find(p => p.type === 'hour')?.value ?? '';
    const min = parts.find(p => p.type === 'minute')?.value ?? '';
    return `${day} ${mon} ${hour}:${min}`;
  } catch {
    const offset = -4 * 3600 * 1000;
    const ld = new Date(d.getTime() + offset);
    return `${ld.getUTCDate()} ${MONTHS_FR[ld.getUTCMonth()]} ${String(ld.getUTCHours()).padStart(2, '0')}:${String(ld.getUTCMinutes()).padStart(2, '0')}`;
  }
}

function fmtLocalTime(d: Date): string {
  // Returns only HH:MM in local time
  try {
    return new Intl.DateTimeFormat('fr-CA', {
      timeZone: 'America/Toronto',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    const offset = -4 * 3600 * 1000;
    const ld = new Date(d.getTime() + offset);
    return `${String(ld.getUTCHours()).padStart(2, '0')}:${String(ld.getUTCMinutes()).padStart(2, '0')}`;
  }
}

function fmtLocalDate(d: Date): string {
  // Returns "20 juin" in local time
  try {
    const parts = new Intl.DateTimeFormat('fr-CA', {
      timeZone: 'America/Toronto',
      day: 'numeric',
      month: 'long',
    }).formatToParts(d);
    const day = parts.find(p => p.type === 'day')?.value ?? '';
    const mon = parts.find(p => p.type === 'month')?.value ?? '';
    return `${day} ${mon}`;
  } catch {
    const offset = -4 * 3600 * 1000;
    const ld = new Date(d.getTime() + offset);
    return `${ld.getUTCDate()} ${MONTHS_FR[ld.getUTCMonth()]}`;
  }
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return fmtLocalDate(a) === fmtLocalDate(b);
}

// ─── Weather code translation ─────────────────────────────────────────────────

const WX_MAP: Record<string, string> = {
  '-RA': 'pluie faible', 'RA': 'pluie', '+RA': 'pluie forte',
  '-SHRA': 'averses de pluie faibles', 'SHRA': 'averses de pluie', '+SHRA': 'fortes averses de pluie',
  'TS': 'orage', 'TSRA': 'orage avec pluie', '+TSRA': 'fort orage avec pluie',
  'TSGR': 'orage avec grêle', 'TSGS': 'orage avec grésil',
  'VCTS': 'orage à proximité', 'VCSH': 'averses à proximité',
  'BR': 'brume', 'FG': 'brouillard', 'HZ': 'brume sèche', 'FU': 'fumée',
  'BCFG': 'brouillard en bancs', 'MIFG': 'brouillard peu épais',
  'PRFG': 'brouillard partiel', 'FZFG': 'brouillard givrant',
  '-SN': 'neige faible', 'SN': 'neige', '+SN': 'neige forte',
  'SHSN': 'averses de neige', 'BLSN': 'poudrerie', 'DRSN': 'poudrerie basse',
  'FZRA': 'pluie verglaçante', 'FZDZ': 'bruine verglaçante',
  '-DZ': 'bruine faible', 'DZ': 'bruine', '+DZ': 'bruine forte',
  'GR': 'grêle', 'GS': 'grésil', 'PL': 'grésil',
  'IC': 'cristaux de glace', 'UP': 'précipitations',
  'RASN': 'pluie et neige', 'SQ': 'grain',
  'SA': 'sable', 'DU': 'poussière',
};

const CLOUD_COVER: Record<string, string> = {
  FEW: 'quelques nuages', SCT: 'nuages épars', BKN: 'ciel fragmenté',
  OVC: 'ciel couvert', VV: 'plafond vertical',
};

export function decodeWeatherPhenomenaToPlainFrench(tokens: string[]): string[] {
  const results: string[] = [];
  for (let t of tokens) {
    if (WX_MAP[t]) {
      results.push(WX_MAP[t]);
    } else {
      // Try compound token decoding
      const decoded = decodeCompoundWx(t);
      if (decoded) results.push(decoded);
    }
  }
  return results;
}

function decodeCompoundWx(token: string): string | null {
  let t = token;
  const parts: string[] = [];
  if (t.startsWith('-')) { parts.push('faible'); t = t.slice(1); }
  else if (t.startsWith('+')) { parts.push('fort(e)'); t = t.slice(1); }
  if (t.startsWith('VC')) { parts.push('à proximité'); t = t.slice(2); }
  const descriptors: Record<string, string> = {
    'SH': 'averses de', 'TS': 'orage avec', 'FZ': 'verglaçant(e)',
    'MI': 'peu épais', 'BC': 'en bancs', 'DR': 'basse', 'BL': 'haute',
  };
  for (const [key, val] of Object.entries(descriptors)) {
    if (t.startsWith(key)) { parts.push(val); t = t.slice(key.length); break; }
  }
  const precip: Record<string, string> = {
    'RA': 'pluie', 'DZ': 'bruine', 'SN': 'neige', 'GR': 'grêle',
    'GS': 'grésil', 'PL': 'grésil', 'BR': 'brume', 'FG': 'brouillard',
    'HZ': 'brume sèche', 'FU': 'fumée', 'TS': 'orage',
  };
  if (precip[t]) parts.push(precip[t]);
  else if (t) parts.push(t.toLowerCase());
  return parts.length > 0 ? parts.join(' ') : null;
}

// ─── Raw TAF segment parser ───────────────────────────────────────────────────

interface RawSegment {
  type: 'MAIN' | 'FM' | 'TEMPO' | 'BECMG' | 'PROB';
  probPercent?: number;
  from: Date | null;
  to: Date | null;
  raw: string;
  windDir?: number | 'VRB';
  windSpd?: number;
  gustSpd?: number;
  visSM?: number;
  visRaw?: string;
  clouds: Array<{ cover: string; altFt: number; cb?: boolean; tcu?: boolean }>;
  wxTokens: string[];
}

function parseTafRaw(rawTaf: string): { validFrom: Date | null; validTo: Date | null; segments: RawSegment[] } {
  if (!rawTaf) return { validFrom: null, validTo: null, segments: [] };
  const now = new Date();

  // Normalize
  const normalized = rawTaf.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();

  // Find validity: DDHH/DDHH
  let validFrom: Date | null = null;
  let validTo: Date | null = null;
  const validMatch = normalized.match(/\b(\d{2})(\d{2})\/(\d{2})(\d{2})\b/);
  if (validMatch) {
    validFrom = buildUTCDate(parseInt(validMatch[1]), parseInt(validMatch[2]), 0, now);
    const tDay = parseInt(validMatch[3]);
    const tHH = parseInt(validMatch[4]);
    validTo = tHH === 24
      ? buildUTCDate(tDay + 1, 0, 0, now)
      : buildUTCDate(tDay, tHH, 0, now);
    if (validTo && validFrom && validTo < validFrom) {
      validTo = new Date(validTo.getTime() + 24 * 3600 * 1000);
    }
  }

  // Split into raw lines by change indicators
  const splitRegex = /\b(FM\d{6}|TEMPO|BECMG|PROB\d{2})\b/g;
  const positions: Array<{ index: number; keyword: string }> = [];
  let m: RegExpExecArray | null;
  while ((m = splitRegex.exec(normalized)) !== null) {
    positions.push({ index: m.index, keyword: m[0] });
  }

  const rawLines: Array<{ keyword: string; text: string }> = [];
  const mainEnd = positions[0]?.index ?? normalized.length;
  rawLines.push({ keyword: 'MAIN', text: normalized.slice(0, mainEnd).trim() });
  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i];
    const nextPos = positions[i + 1];
    const text = nextPos
      ? normalized.slice(pos.index, nextPos.index).trim()
      : normalized.slice(pos.index).trim();
    rawLines.push({ keyword: pos.keyword, text });
  }

  const segments: RawSegment[] = rawLines.map((rl, idx) => {
    const seg = parseSegmentLine(rl.keyword, rl.text, now, validFrom, validTo);
    return seg;
  });

  // Fix FM segments: end = next FM/TEMPO/BECMG start or validTo
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if ((seg.type === 'FM' || seg.type === 'MAIN') && seg.to === null) {
      // Find next base segment (FM or MAIN)
      let nextBase: Date | null = null;
      for (let j = i + 1; j < segments.length; j++) {
        if (segments[j].type === 'FM' || segments[j].type === 'BECMG') {
          nextBase = segments[j].from;
          break;
        }
      }
      seg.to = nextBase ?? validTo;
    }
  }

  return { validFrom, validTo, segments };
}

function parseSegmentLine(keyword: string, text: string, now: Date, validFrom: Date | null, validTo: Date | null): RawSegment {
  const tokens = text.split(/\s+/).filter(Boolean);
  let type: RawSegment['type'] = 'MAIN';
  let probPercent: number | undefined;
  let from: Date | null = null;
  let to: Date | null = null;
  let tokenStart = 0;

  if (keyword === 'MAIN') {
    type = 'MAIN';
    from = validFrom;
    to = validTo;
    // Skip station, type, issue time, validity
    // Find first weather token (skip TAF ICAO DDHHmmZ DDHH/DDHH)
    for (let i = 0; i < tokens.length; i++) {
      if (/^\d{4}\/\d{4}$/.test(tokens[i])) { tokenStart = i + 1; break; }
    }
  } else if (keyword.startsWith('FM')) {
    type = 'FM';
    const fmM = keyword.match(/^FM(\d{2})(\d{2})(\d{2})$/);
    if (fmM) {
      from = buildUTCDate(parseInt(fmM[1]), parseInt(fmM[2]), parseInt(fmM[3]), now);
    }
    to = null; // will be filled later
    tokenStart = 1; // skip FM token
  } else if (keyword === 'TEMPO') {
    type = 'TEMPO';
    tokenStart = 1;
    const timeToken = tokens[1];
    if (timeToken && /^\d{4}\/\d{4}$/.test(timeToken)) {
      const [f, t2] = timeToken.split('/');
      from = buildUTCDate(parseInt(f.slice(0, 2)), parseInt(f.slice(2, 4)), 0, now);
      const tDay = parseInt(t2.slice(0, 2));
      const tHH = parseInt(t2.slice(2, 4));
      to = tHH === 24 ? buildUTCDate(tDay + 1, 0, 0, now) : buildUTCDate(tDay, tHH, 0, now);
      if (to && from && to < from) to = new Date(to.getTime() + 24 * 3600 * 1000);
      tokenStart = 2;
    }
  } else if (keyword === 'BECMG') {
    type = 'BECMG';
    tokenStart = 1;
    const timeToken = tokens[1];
    if (timeToken && /^\d{4}\/\d{4}$/.test(timeToken)) {
      const [f, t2] = timeToken.split('/');
      from = buildUTCDate(parseInt(f.slice(0, 2)), parseInt(f.slice(2, 4)), 0, now);
      const tDay = parseInt(t2.slice(0, 2));
      const tHH = parseInt(t2.slice(2, 4));
      to = tHH === 24 ? buildUTCDate(tDay + 1, 0, 0, now) : buildUTCDate(tDay, tHH, 0, now);
      if (to && from && to < from) to = new Date(to.getTime() + 24 * 3600 * 1000);
      tokenStart = 2;
    }
  } else if (/^PROB\d{2}$/.test(keyword)) {
    type = 'PROB';
    probPercent = parseInt(keyword.replace('PROB', ''));
    tokenStart = 1;
    // Check for TEMPO after PROB
    if (tokens[1] === 'TEMPO') tokenStart = 2;
    const timeToken = tokens[tokenStart];
    if (timeToken && /^\d{4}\/\d{4}$/.test(timeToken)) {
      const [f, t2] = timeToken.split('/');
      from = buildUTCDate(parseInt(f.slice(0, 2)), parseInt(f.slice(2, 4)), 0, now);
      const tDay = parseInt(t2.slice(0, 2));
      const tHH = parseInt(t2.slice(2, 4));
      to = tHH === 24 ? buildUTCDate(tDay + 1, 0, 0, now) : buildUTCDate(tDay, tHH, 0, now);
      if (to && from && to < from) to = new Date(to.getTime() + 24 * 3600 * 1000);
      tokenStart++;
    }
  }

  // Parse weather from remaining tokens
  const remaining = tokens.slice(tokenStart);
  let windDir: number | 'VRB' | undefined;
  let windSpd: number | undefined;
  let gustSpd: number | undefined;
  let visSM: number | undefined;
  let visRaw: string | undefined;
  const clouds: RawSegment['clouds'] = [];
  const wxTokens: string[] = [];

  for (let t of remaining) {
    if (t === 'RMK') break;

    // Wind
    const windM = t.match(/^(\d{3}|VRB)(\d{2,3})(G(\d{2,3}))?KT$/);
    if (windM) {
      windDir = windM[1] === 'VRB' ? 'VRB' : parseInt(windM[1]);
      windSpd = parseInt(windM[2]);
      gustSpd = windM[4] ? parseInt(windM[4]) : 0;
      continue;
    }

    // Visibility P6SM
    if (t === 'P6SM') { visSM = 7; visRaw = 'P6SM'; continue; }
    if (t === 'CAVOK' || t === '9999') { visSM = 15; visRaw = t; continue; }

    // Visibility SM
    const visSMM = t.match(/^(\d+(?:\/\d+)?)SM$/);
    if (visSMM) {
      const parts = visSMM[1].split('/');
      visSM = parts.length === 2 ? parseInt(parts[0]) / parseInt(parts[1]) : parseInt(parts[0]);
      visRaw = t;
      continue;
    }

    // Metric visibility
    const visMetM = t.match(/^(\d{4})$/);
    if (visMetM) {
      const metres = parseInt(t);
      if (metres >= 100 && metres <= 9999) {
        visSM = metres / 1609.34;
        visRaw = t;
        continue;
      }
    }

    // Clouds
    const cloudM = t.match(/^(FEW|SCT|BKN|OVC|VV)(\d{3})(CB|TCU)?$/);
    if (cloudM) {
      clouds.push({
        cover: cloudM[1],
        altFt: parseInt(cloudM[2]) * 100,
        cb: cloudM[3] === 'CB',
        tcu: cloudM[3] === 'TCU',
      });
      continue;
    }
    if (t === 'SKC' || t === 'CLR' || t === 'NSC') {
      clouds.push({ cover: 'CLR', altFt: 99999 });
      continue;
    }

    // Weather phenomena
    if (WX_MAP[t] || /^[-+]?(VC)?(MI|PR|BC|DR|BL|SH|TS|FZ)?(DZ|RA|SN|SG|IC|PL|GR|GS|UP|BR|FG|FU|VA|DU|SA|HZ|PO|SQ|FC|SS|DS|TS)+$/.test(t)) {
      wxTokens.push(t);
    }
  }

  return { type, probPercent, from, to, raw: text, windDir, windSpd, gustSpd, visSM, visRaw, clouds, wxTokens };
}

// ─── Decode helpers ───────────────────────────────────────────────────────────

function decodeWind(seg: RawSegment): string {
  if (seg.windSpd === undefined) return '';
  if (seg.windSpd === 0) return 'Calme';
  const dir = seg.windDir === 'VRB' ? 'Variable' : seg.windDir !== undefined ? `${seg.windDir}°` : 'Variable';
  let s = `${dir} / ${seg.windSpd} kt`;
  if (seg.gustSpd && seg.gustSpd > 0) s += `, rafales ${seg.gustSpd} kt`;
  return s;
}

function decodeVisibility(seg: RawSegment): string {
  if (seg.visSM === undefined) return '';
  if (seg.visRaw === 'P6SM' || seg.visSM >= 7) return 'supérieure à 6 SM';
  if (seg.visSM >= 6) return `${seg.visRaw ?? seg.visSM + ' SM'}`;
  if (seg.visSM >= 5) return `${seg.visSM} SM — réduite`;
  if (seg.visSM >= 3) return `${seg.visSM} SM — mauvaise`;
  if (seg.visSM >= 1) return `${seg.visSM} SM — très mauvaise`;
  return `${seg.visSM.toFixed(1)} SM — très mauvaise`;
}

function decodeClouds(seg: RawSegment): string {
  if (seg.clouds.length === 0) return '';
  const parts = seg.clouds
    .filter(c => c.cover !== 'CLR')
    .map(c => {
      const label = CLOUD_COVER[c.cover] ?? c.cover;
      const alt = c.altFt < 99999 ? ` à ${c.altFt} ft` : '';
      const cb = c.cb ? ' avec cumulonimbus' : c.tcu ? ' avec cumulus bourgeonnant' : '';
      return `${label}${alt}${cb}`;
    });
  if (parts.length === 0) return 'Ciel dégagé';
  return parts.join(', ');
}

function decodeWx(seg: RawSegment): string {
  const decoded = decodeWeatherPhenomenaToPlainFrench(seg.wxTokens);
  return decoded.join(', ');
}

function getCeilingFt(seg: RawSegment): number {
  const bknOvc = seg.clouds.find(c => c.cover === 'BKN' || c.cover === 'OVC' || c.cover === 'VV');
  if (bknOvc) return bknOvc.altFt;
  const sct = seg.clouds.find(c => c.cover === 'SCT');
  if (sct) return sct.altFt;
  return 99999;
}

function hasCB(seg: RawSegment): boolean {
  return seg.clouds.some(c => c.cb) || seg.wxTokens.some(t => /TSRA|TSGR|TSGS/.test(t));
}

function hasThunderstorm(seg: RawSegment): boolean {
  return seg.wxTokens.some(t => /^[+-]?(TS|TSRA|TSGR|TSGS|VCTS)/.test(t));
}

function hasMist(seg: RawSegment): boolean {
  return seg.wxTokens.some(t => t === 'BR' || t === 'FG' || t === 'BCFG' || t === 'MIFG' || t === 'FZFG');
}

function hasShowers(seg: RawSegment): boolean {
  return seg.wxTokens.some(t => /SHRA|SHSN/.test(t));
}

function hasRain(seg: RawSegment): boolean {
  return seg.wxTokens.some(t => /RA|DZ/.test(t));
}

function hasFreezing(seg: RawSegment): boolean {
  return seg.wxTokens.some(t => /FZRA|FZDZ|FZFG/.test(t));
}

function hasSnow(seg: RawSegment): boolean {
  return seg.wxTokens.some(t => /SN|SHSN|BLSN/.test(t));
}

// ─── Risk level ───────────────────────────────────────────────────────────────

function getTafRiskLevel(
  baseSeg: RawSegment,
  tempoSegs: RawSegment[],
  probSegs: RawSegment[]
): RiskLevel {
  // Check for thunderstorm/CB in any segment
  const allSegs = [baseSeg, ...tempoSegs, ...probSegs];
  if (allSegs.some(s => hasThunderstorm(s) || hasCB(s))) return 'Risque accru';

  // Check for freezing precip
  if (allSegs.some(s => hasFreezing(s))) return 'Risque accru';

  // Check for PROB with dangerous conditions
  if (probSegs.some(s => hasThunderstorm(s) || hasCB(s) || (s.gustSpd && s.gustSpd >= 30))) return 'Prudence';

  // Check for TEMPO with significant degradation
  if (tempoSegs.some(s => {
    const vis = s.visSM ?? 99;
    const ceil = getCeilingFt(s);
    return vis < 3 || ceil < 1000 || hasThunderstorm(s) || hasCB(s);
  })) return 'Prudence';

  // Check for TEMPO with moderate degradation
  if (tempoSegs.some(s => {
    const vis = s.visSM ?? 99;
    const ceil = getCeilingFt(s);
    return vis < 5 || ceil < 3000 || hasMist(s) || hasShowers(s);
  })) return 'À surveiller';

  // Check base conditions
  const baseVis = baseSeg.visSM ?? 99;
  const baseCeil = getCeilingFt(baseSeg);
  if (baseVis < 3 || baseCeil < 1000) return 'Risque accru';
  if (baseVis < 5 || baseCeil < 3000) return 'Prudence';
  if (hasMist(baseSeg) || hasShowers(baseSeg)) return 'À surveiller';

  // Check PROB with moderate conditions
  if (probSegs.some(s => hasMist(s) || hasShowers(s) || hasRain(s))) return 'À surveiller';

  return 'Favorable';
}

// ─── Icon selection ───────────────────────────────────────────────────────────

function getTafWeatherIcon(
  baseSeg: RawSegment,
  tempoSegs: RawSegment[],
  probSegs: RawSegment[]
): IconName {
  const allSegs = [baseSeg, ...tempoSegs, ...probSegs];

  // Priority order
  if (allSegs.some(s => hasThunderstorm(s) || hasCB(s))) return 'thunderstorm';
  if (allSegs.some(s => hasFreezing(s))) return 'freezingRain';
  if (allSegs.some(s => hasSnow(s))) return 'snow';

  // Fog/mist
  if (allSegs.some(s => s.wxTokens.some(t => t === 'FG' || t === 'BCFG' || t === 'MIFG'))) return 'fog';

  // Showers/rain
  if (allSegs.some(s => hasShowers(s))) return 'showers';
  if (allSegs.some(s => hasRain(s))) return 'rain';
  if (allSegs.some(s => hasMist(s))) return 'mist';

  // Cloud cover from base
  const baseCeil = getCeilingFt(baseSeg);
  const baseHasBkn = baseSeg.clouds.some(c => c.cover === 'BKN' || c.cover === 'OVC');
  const baseHasSct = baseSeg.clouds.some(c => c.cover === 'SCT');
  if (baseHasBkn && baseCeil < 5000) return 'cloud';
  if (baseHasBkn || baseHasSct) return 'partlyCloudy';

  return 'clear';
}

// ─── Title builder ────────────────────────────────────────────────────────────

function buildPeriodTitle(
  from: Date | null,
  to: Date | null,
  isFirst: boolean
): string {
  if (!from) return 'Plage horaire non décodée';

  const localFrom = fmtLocalTime(from);
  const dateFrom = fmtLocalDate(from);

  if (!to) {
    return `À partir de ${localFrom} locale`;
  }

  const localTo = fmtLocalTime(to);
  const dateTo = fmtLocalDate(to);

  if (isFirst) {
    // "Du 20 juin 11:00 à 12:00 locale" or "Du 20 juin 11:00 au 21 juin 08:00 locale"
    if (isSameLocalDay(from, to)) {
      return `Du ${dateFrom} ${localFrom} à ${localTo} locale`;
    }
    return `Du ${dateFrom} ${localFrom} au ${dateTo} ${localTo} locale`;
  }

  // Check if same day
  if (isSameLocalDay(from, to)) {
    return `Entre ${localFrom} et ${localTo} locale`;
  }

  // Different days
  return `Du ${dateFrom} ${localFrom} au ${dateTo} ${localTo} locale`;
}

function buildZuluLabel(from: Date | null, to: Date | null): string {
  if (!from) return '';
  if (!to) return `Zoulou : à partir du ${fmtZulu(from)}`;
  return `Zoulou : ${fmtZulu(from)} → ${fmtZulu(to)}`;
}

// ─── Summary builder ──────────────────────────────────────────────────────────

function buildSummary(
  baseSeg: RawSegment,
  tempoSegs: RawSegment[],
  probSegs: RawSegment[],
  riskLevel: RiskLevel
): string {
  const baseVis = baseSeg.visSM ?? 99;
  const baseCeil = getCeilingFt(baseSeg);
  const isVFR = baseVis >= 5 && baseCeil >= 3000;
  const isMVFR = !isVFR && (baseVis >= 3 || baseCeil >= 1000);

  const hasTempoRisk = tempoSegs.some(s =>
    hasThunderstorm(s) || hasCB(s) || hasMist(s) || hasShowers(s) || hasRain(s) ||
    (s.visSM !== undefined && s.visSM < 5) || getCeilingFt(s) < 3000
  );
  const hasProbRisk = probSegs.some(s =>
    hasThunderstorm(s) || hasCB(s) || hasMist(s) || hasShowers(s) ||
    (s.visSM !== undefined && s.visSM < 5)
  );
  const hasProbStorm = probSegs.some(s => hasThunderstorm(s) || hasCB(s));
  const hasTempoStorm = tempoSegs.some(s => hasThunderstorm(s) || hasCB(s));

  if (riskLevel === 'Risque accru') {
    if (hasThunderstorm(baseSeg) || hasCB(baseSeg)) return 'Conditions orageuses prévues. Vol déconseillé.';
    if (hasFreezing(baseSeg)) return 'Précipitations verglaçantes prévues. Conditions dangereuses.';
    if (hasProbStorm) return 'Conditions généralement VFR, mais risque élevé d\'orage et de cumulonimbus.';
    return 'Conditions dégradées prévues. Prudence maximale.';
  }

  if (riskLevel === 'Prudence') {
    if (hasTempoStorm || hasProbStorm) return 'Conditions VFR générales, mais dégradations possibles avec averses, brume et risque d\'orage.';
    if (hasTempoRisk) return 'Conditions généralement VFR, mais dégradations temporaires possibles.';
    return 'Conditions acceptables, mais à surveiller.';
  }

  if (riskLevel === 'À surveiller') {
    if (hasTempoRisk || hasProbRisk) return 'Conditions VFR générales, mais dégradations possibles par moments.';
    if (hasMist(baseSeg)) return 'Conditions VFR avec brume possible.';
    if (hasShowers(baseSeg)) return 'Conditions VFR avec averses possibles.';
    return 'Conditions généralement favorables, mais à surveiller.';
  }

  // Favorable
  if (isVFR) return 'Conditions généralement favorables.';
  if (isMVFR) return 'Conditions marginales VFR.';
  return 'Conditions acceptables.';
}

// ─── Badge builder ────────────────────────────────────────────────────────────

function buildBadges(
  baseSeg: RawSegment,
  tempoSegs: RawSegment[],
  probSegs: RawSegment[],
  riskLevel: RiskLevel
): string[] {
  const badges: string[] = [];
  const allSegs = [baseSeg, ...tempoSegs, ...probSegs];

  if (allSegs.some(s => hasThunderstorm(s))) badges.push('Risque orage');
  if (allSegs.some(s => hasCB(s))) badges.push('Cumulonimbus');
  if (allSegs.some(s => hasFreezing(s))) badges.push('Verglas');
  if (allSegs.some(s => hasSnow(s))) badges.push('Neige');

  const maxGust = Math.max(
    baseSeg.gustSpd ?? 0,
    ...tempoSegs.map(s => s.gustSpd ?? 0),
    ...probSegs.map(s => s.gustSpd ?? 0)
  );
  if (maxGust >= 25) badges.push(`Rafales ${maxGust} kt`);

  if (allSegs.some(s => hasShowers(s))) badges.push('Averses possibles');
  if (allSegs.some(s => hasMist(s))) badges.push('Brume');
  if (allSegs.some(s => s.wxTokens.includes('FG'))) badges.push('Brouillard');

  // Visibility
  const minVis = Math.min(
    baseSeg.visSM ?? 99,
    ...tempoSegs.map(s => s.visSM ?? 99),
    ...probSegs.map(s => s.visSM ?? 99)
  );
  if (minVis < 3) badges.push('Visibilité réduite');

  // Ceiling
  const minCeil = Math.min(
    getCeilingFt(baseSeg),
    ...tempoSegs.map(s => getCeilingFt(s)),
    ...probSegs.map(s => getCeilingFt(s))
  );
  if (minCeil < 1500) badges.push('Nuages bas');

  // Risk level badge
  if (riskLevel === 'Favorable') badges.push('VFR général');
  else if (riskLevel === 'À surveiller') badges.push('À surveiller');
  else if (riskLevel === 'Prudence') badges.push('Prudence');
  else if (riskLevel === 'Risque accru') badges.push('Risque accru');

  return badges;
}

// ─── Watch items ──────────────────────────────────────────────────────────────

function buildWatchItems(
  baseSeg: RawSegment,
  tempoSegs: RawSegment[],
  probSegs: RawSegment[]
): string[] {
  const items: string[] = [];
  const allSegs = [baseSeg, ...tempoSegs, ...probSegs];

  if (allSegs.some(s => hasThunderstorm(s))) items.push('orage');
  if (allSegs.some(s => hasCB(s))) items.push('cumulonimbus');
  if (allSegs.some(s => hasFreezing(s))) items.push('précipitations verglaçantes');

  const maxGust = Math.max(
    baseSeg.gustSpd ?? 0,
    ...tempoSegs.map(s => s.gustSpd ?? 0),
    ...probSegs.map(s => s.gustSpd ?? 0)
  );
  if (maxGust >= 25) items.push(`rafales ${maxGust} kt`);

  if (allSegs.some(s => hasShowers(s))) items.push('averses');
  if (allSegs.some(s => hasMist(s))) items.push('brume');
  if (allSegs.some(s => s.wxTokens.includes('FG'))) items.push('brouillard');

  const minVis = Math.min(
    baseSeg.visSM ?? 99,
    ...tempoSegs.map(s => s.visSM ?? 99),
    ...probSegs.map(s => s.visSM ?? 99)
  );
  if (minVis < 5) items.push('visibilité réduite');

  const minCeil = Math.min(
    getCeilingFt(baseSeg),
    ...tempoSegs.map(s => getCeilingFt(s)),
    ...probSegs.map(s => getCeilingFt(s))
  );
  if (minCeil < 1500) items.push('nuages bas');

  return items;
}

// ─── TEMPO/PROB text builders ─────────────────────────────────────────────────

function buildTempoText(tempoSegs: RawSegment[], parentFrom: Date | null, parentTo: Date | null): string {
  if (tempoSegs.length === 0) return '';
  const parts: string[] = [];
  for (const seg of tempoSegs) {
    const items: string[] = [];
    if (seg.visSM !== undefined && seg.visSM < 7) {
      items.push(`visibilité ${seg.visRaw === 'P6SM' ? 'supérieure à 6 SM' : seg.visSM + ' SM'}`);
    }
    const wx = decodeWx(seg);
    if (wx) items.push(wx);
    const clouds = decodeClouds(seg);
    if (clouds && clouds !== 'Ciel dégagé') items.push(clouds);

    if (items.length > 0) {
      // Build time label
      let timeLabel = '';
      if (seg.from && seg.to) {
        const sameAsParent = parentFrom && parentTo &&
          seg.from.getTime() === parentFrom.getTime() &&
          seg.to.getTime() === parentTo.getTime();
        if (!sameAsParent) {
          const localFrom = fmtLocalTime(seg.from);
          const localTo = fmtLocalTime(seg.to);
          if (isSameLocalDay(seg.from, seg.to)) {
            timeLabel = ` entre ${localFrom} et ${localTo} locale`;
          } else {
            timeLabel = ` du ${fmtLocalDate(seg.from)} ${localFrom} au ${fmtLocalDate(seg.to)} ${localTo} locale`;
          }
        }
      }
      parts.push(`Temporairement possible${timeLabel} : ${items.join(', ')}`);
    }
  }
  return parts.join(' | ');
}

function buildProbText(probSegs: RawSegment[], parentFrom: Date | null, parentTo: Date | null): string {
  if (probSegs.length === 0) return '';
  const parts: string[] = [];
  for (const seg of probSegs) {
    const pct = seg.probPercent ?? 30;
    const items: string[] = [];
    const wx = decodeWx(seg);
    if (wx) items.push(wx);
    if (seg.visSM !== undefined && seg.visSM < 7) {
      items.push(`visibilité ${seg.visRaw === 'P6SM' ? 'supérieure à 6 SM' : seg.visSM + ' SM'}`);
    }
    const clouds = decodeClouds(seg);
    if (clouds && clouds !== 'Ciel dégagé') items.push(clouds);
    if (seg.gustSpd && seg.gustSpd > 0) items.push(`rafales jusqu'à ${seg.gustSpd} kt`);

    if (items.length > 0) {
      let timeLabel = '';
      if (seg.from && seg.to) {
        const sameAsParent = parentFrom && parentTo &&
          seg.from.getTime() === parentFrom.getTime() &&
          seg.to.getTime() === parentTo.getTime();
        if (!sameAsParent) {
          const localFrom = fmtLocalTime(seg.from);
          const localTo = fmtLocalTime(seg.to);
          if (isSameLocalDay(seg.from, seg.to)) {
            timeLabel = ` entre ${localFrom} et ${localTo} locale`;
          } else {
            timeLabel = ` du ${fmtLocalDate(seg.from)} ${localFrom} au ${fmtLocalDate(seg.to)} ${localTo} locale`;
          }
        }
      }
      parts.push(`Risque d'environ ${pct} %${timeLabel} : ${items.join(', ')}`);
    }
  }
  return parts.join(' | ');
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Build a plain-language TAF timeline from a raw TAF string.
 * Returns an array of PlainTafPeriod objects, one per base period,
 * with TEMPO/PROB merged into their parent period.
 */
export function buildPlainLanguageTafTimeline(rawTaf: string): PlainTafPeriod[] {
  if (!rawTaf || rawTaf.trim() === '') return [];

  const { validFrom, validTo, segments } = parseTafRaw(rawTaf);

  // Separate base segments (MAIN, FM, BECMG) from modifiers (TEMPO, PROB)
  const baseSegments = segments.filter(s => s.type === 'MAIN' || s.type === 'FM' || s.type === 'BECMG');
  const tempoSegments = segments.filter(s => s.type === 'TEMPO');
  const probSegments = segments.filter(s => s.type === 'PROB');

  if (baseSegments.length === 0) return [];

  const periods: PlainTafPeriod[] = [];

  for (let i = 0; i < baseSegments.length; i++) {
    const baseSeg = baseSegments[i];
    let from = baseSeg.from;
    let to = baseSeg.to;

    // Find TEMPO segments that overlap with this base period
    const relatedTempo = tempoSegments.filter(t => {
      if (!t.from || !t.to) return false;
      if (!from) return false;
      const periodEnd = to ?? validTo;
      if (!periodEnd) return true;
      // Overlap: tempo starts before period ends AND tempo ends after period starts
      return t.from < periodEnd && t.to > from;
    });

    // Find PROB segments that overlap with this base period
    const relatedProb = probSegments.filter(p => {
      if (!p.from || !p.to) return false;
      if (!from) return false;
      const periodEnd = to ?? validTo;
      if (!periodEnd) return true;
      return p.from < periodEnd && p.to > from;
    });

    const riskLevel = getTafRiskLevel(baseSeg, relatedTempo, relatedProb);
    const iconName = getTafWeatherIcon(baseSeg, relatedTempo, relatedProb);

    const title = buildPeriodTitle(from, to, i === 0);
    const zuluLabel = buildZuluLabel(from, to);
    const summary = buildSummary(baseSeg, relatedTempo, relatedProb, riskLevel);

    const windText = decodeWind(baseSeg);
    const visibilityText = decodeVisibility(baseSeg);
    const cloudsText = decodeClouds(baseSeg);
    const weatherText = decodeWx(baseSeg);
    const temporaryText = buildTempoText(relatedTempo, from, to);
    const probabilityText = buildProbText(relatedProb, from, to);
    const watchItems = buildWatchItems(baseSeg, relatedTempo, relatedProb);
    const badges = buildBadges(baseSeg, relatedTempo, relatedProb, riskLevel);

    const startLocal = from ? fmtLocal(from) : '';
    const endLocal = to ? fmtLocal(to) : '';

    periods.push({
      id: `period-${i}`,
      startZulu: from,
      endZulu: to,
      startLocal,
      endLocal,
      title,
      zuluLabel,
      summary,
      windText,
      visibilityText,
      cloudsText,
      weatherText,
      temporaryText,
      probabilityText,
      watchItems,
      riskLevel,
      iconName,
      badges,
    });
  }

  return periods;
}
